import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ApiConnectionString } from "../../../constants/ApiConntection";

export default function QualificationsCrud({ user, qualifications, setQualifications, onHasQualificationChange, error }) {
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const updateTimersRef = useRef({});

  const getAuthConfig = () => {
    return user?.token ? { headers: { Authorization: `Bearer ${user.token}` } } : undefined;
  };

  const normaliseQualification = (q) => {
    if (!q) return null;
    const id = q.id ?? q.Id ?? q.qualificationId ?? q.QualificationId;
    const qName = q.name ?? q.Name;
    return { ...q, id, name: qName };
  };

  const fetchQualifications = async () => {
    try {
      const res = await axios.get(
        `${ApiConnectionString}/api/qualifications`,
        getAuthConfig()
      );

      const list = Array.isArray(res?.data) ? res.data : [];
      setQualifications(list);
    } catch {
      setLocalError("Could not load qualifications");
    }
  };

  const createQualification = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setLocalError("");
    setIsLoading(true);

    try {
      await axios.post(
        `${ApiConnectionString}/api/qualification`,
        { name: trimmed },
        getAuthConfig()
      );

      setName("");

      await fetchQualifications();
    } catch {
      setLocalError("Could not create qualification");
    } finally {
      setIsLoading(false);
    }
  };

  const updateQualification = async ({ id, name }) => {
    if (!id) return;

    try {
      await axios.put(
        `${ApiConnectionString}/api/qualification`,
        { id, name },
        getAuthConfig()
      );
    } catch {
      setLocalError("Could not update qualification");
    }
  };

  const scheduleQualificationUpdate = (id, nextName) => {
    if (!id) return;

    if (updateTimersRef.current[id]) clearTimeout(updateTimersRef.current[id]);

    updateTimersRef.current[id] = setTimeout(() => {
      updateQualification({ id, name: nextName });
      updateTimersRef.current[id] = null;
    }, 500);
  };

  const flushQualificationUpdate = (id) => {
    if (!id) return;

    if (updateTimersRef.current[id]) {
      clearTimeout(updateTimersRef.current[id]);
      updateTimersRef.current[id] = null;
    }

    const q = (Array.isArray(qualifications) ? qualifications : []).find((x) => (x.id ?? x.Id) === id);
    const normalised = normaliseQualification(q);

    if (normalised) updateQualification({ id: normalised.id, name: normalised.name || "" });
  };

  const deleteQualification = async (id) => {
    setLocalError("");
    setIsLoading(true);
    try {
      await axios.delete(
        `${ApiConnectionString}/api/qualification/${id}`,
        getAuthConfig()
      );

      setQualifications((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.filter((x) => (x.id ?? x.Id) !== id);
      });

      if (editingId === id) setEditingId(null);
    } catch {
      setLocalError("Could not delete qualification");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onHasQualificationChange) onHasQualificationChange((Array.isArray(qualifications) ? qualifications : []).length > 0);
  }, [Array.isArray(qualifications) ? qualifications.length : 0]);

  useEffect(() => {
    return () => {
      Object.values(updateTimersRef.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  const list = (Array.isArray(qualifications) ? qualifications : []).map(normaliseQualification).filter(Boolean);

  return (
    <div className="ppfShell w-100">
      <div className="ppfCard">
        <div className="ppfHead">
          <div className="ppfHeadLeft">
            <h3 className="ppfTitle mb-0">Qualifications</h3>
          </div>
        </div>

        <div className="ppfBody">
          <div className="mb-3">
            <label className="form-label ppfLabel mb-1">Add qualification</label>

            <div className="input-group">
              <input
                className="form-control form-control-sm ppfInput"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Level 3 PT"
                disabled={isLoading}
              />
              <button
                className="btn ppfBtnPrimary btn-sm"
                onClick={createQualification}
                disabled={isLoading || !name.trim()}
                type="button"
              >
                Add
              </button>
            </div>
          </div>

          {localError ? <div className="alert alert-danger pp-upload-alert mb-3">{localError}</div> : null}
          {error ? <div className="invalid-feedback d-block mb-3">{error}</div> : null}

          {isLoading && list.length === 0 ? (
            <div className="ppfHelpText">Loading...</div>
          ) : list.length === 0 ? (
            <div className="ppfHelpText">No qualifications yet</div>
          ) : (
            <div className="list-group">
              {list.map((q) => {
                const isEditing = editingId === q.id;

                return (
                  <div key={q.id} className="list-group-item py-2">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        className="form-control form-control-sm ppfInput"
                        value={q.name || ""}
                        disabled={isLoading || !isEditing}
                        onChange={(e) => {
                          const next = e.target.value;

                          setQualifications((prev) => {
                            const arr = Array.isArray(prev) ? prev : [];
                            return arr.map((x) => {
                              const xId = x.id ?? x.Id ?? x.qualificationId ?? x.QualificationId;
                              return xId === q.id ? { ...x, name: next, Name: next } : x;
                            });
                          });

                          scheduleQualificationUpdate(q.id, next);
                        }}
                        onBlur={() => {
                          if (isEditing) flushQualificationUpdate(q.id);
                        }}
                      />

                      {!isEditing ? (
                        <button className="btn ppfBtnOutline btn-sm" type="button" onClick={() => setEditingId(q.id)} disabled={isLoading}>
                          Edit
                        </button>
                      ) : (
                        <button
                          className="btn ppfBtnPrimary btn-sm"
                          type="button"
                          onClick={() => {
                            flushQualificationUpdate(q.id);
                            setEditingId(null);
                          }}
                          disabled={isLoading}
                        >
                          Done
                        </button>
                      )}

                      <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => deleteQualification(q.id)} disabled={isLoading}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <span className="ppfHelpText mb-0">Edits auto save while editing</span>
            <span className="ppfStepPill">{list.length} total</span>
          </div>
        </div>
      </div>
    </div>
  );
}
